'use client';

import { MouseEvent, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Button, Loader } from 'ui';
import { useTranslation } from '@/i18n/client';
import { CartProduct } from './components/cartProduct';
import { formatPrice } from 'utils';
import { useBasket } from '@/shared/context/basket-context';
import { routes } from '@/shared/data/routes';
import { useCurrentLocale } from '@/shared/utils/client';
import Close from 'icons/lib/close_nav.svg';

import styles from './HeaderCart.module.scss';
import { HeaderCartProps } from './types';
import { ShippingPrice } from '../../../shippingPrice/ShippingPrice';

export const HeaderCart = ({
  onCloseButtonPress,
  shippingMethods,
}: HeaderCartProps) => {
  const locale = useCurrentLocale();
  const router = useRouter();
  const { t } = useTranslation('header');
  const productsWrapperRef = useRef<HTMLDivElement>(null);
  const {
    miniBasket,
    freeShippingPrice,
    isMiniBasketReloading,
    setIsCartSidebarOpen,
  } = useBasket();

  const hasFreeShipping =
    (miniBasket?.productsPrice?.amount || 0) >= freeShippingPrice.amount;

  const handleGoToCartPress = (event: MouseEvent) => {
    event.preventDefault();
    setIsCartSidebarOpen(false);
    router.refresh();
    router.push(routes[locale].CART);
  };

  return (
    <>
      <div className={styles.headerCart}>
        <div className={styles.headerCart__header}>
          <h3 className={styles['headerCart__header--title']}>
            {t('cartSidebar.yourCart')}
          </h3>
          <Button
            size="icon"
            variant="iconButton"
            onPress={onCloseButtonPress}
            buttonName="HeaderCart__CloseButton"
          >
            <Close />
          </Button>
        </div>
        <div className={styles.headerCart__productSection}>
          {(miniBasket?.items || []).map((product) => (
            <CartProduct key={product.productSlug} product={product} />
          ))}
        </div>
        <div className={styles.headerCart__bottomSection}>
          <div className={styles.headerCart__bottomSection__goToCart}>
            <div
              className={
                styles['headerCart__bottomSection__goToCart--priceWrapper']
              }
            >
              {t('cartSidebar.toPayTotal')}
              <b>
                {miniBasket
                  ? formatPrice(
                      miniBasket.totalPrice?.amount,
                      miniBasket.locale,
                      miniBasket.totalPrice?.currency
                    )
                  : formatPrice(0)}
              </b>
            </div>
            <div
              className={
                styles['headerCart__bottomSection__goToCart--priceWrapper']
              }
            >
              {t('cartSidebar.shipmentCost')}{' '}
              <b>
                {miniBasket && (
                  <ShippingPrice
                    basketItems={miniBasket.items}
                    basketLocale={miniBasket.locale}
                    basketShippingPrice={miniBasket.shippingPrice}
                    hasShippingMethodSelected={!!miniBasket.shippingMethod}
                    shippingMethods={shippingMethods}
                    hasFreeShipping={hasFreeShipping}
                  />
                )}
              </b>
            </div>

            <Button
              as={Link}
              href={routes[locale].CART}
              buttonName="HeaderCart__GoToCartButton"
              fullWidth
              size="big"
              variant="leaf"
              isDisabled={!miniBasket?.items?.length}
              onPress={handleGoToCartPress}
            >
              {t('cartSidebar.goToCart')}
            </Button>
          </div>
        </div>
      </div>
      {isMiniBasketReloading && <Loader />}
    </>
  );
};
